/* Apollo.io vault — master key + credit-meters. */
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { KeyRound, CheckCircle2, AlertTriangle, RefreshCw, Loader2, Eye, EyeOff, Plug, Unplug, FlaskConical } from "lucide-react";
import { CreditMeter, SubSection } from "./VaultBits";

/* ----------------------------------------------------------------- */
/*  Apollo MCP — officiële MCP-server via OAuth 2.0 (geen API-key)   */
/* ----------------------------------------------------------------- */
function ApolloMcpSection() {
    const { t } = useTranslation();
    const [status, setStatus] = useState(null);      // null = loading
    const [busy, setBusy] = useState(false);
    const [testBusy, setTestBusy] = useState(false);
    const [testResult, setTestResult] = useState(null);

    const loadStatus = async () => {
        try {
            const r = await api.get("/integrations/apollo-mcp/status");
            setStatus(r.data);
        } catch (e) { setStatus({ connected: false, status: "error" }); }
    };
    useEffect(() => { loadStatus(); }, []);

    const connect = async () => {
        setBusy(true);
        try {
            const r = await api.post("/integrations/apollo-mcp/start");
            window.location.assign(r.data.authorization_url);
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("integr.mcp_start_failed"));
            setBusy(false);
        }
    };
    const disconnect = async () => {
        if (!window.confirm(t("integr.mcp_disconnect_confirm"))) return;
        setBusy(true);
        try {
            await api.post("/integrations/apollo-mcp/disconnect");
            toast.success(t("integr.mcp_disconnected"));
            setTestResult(null);
            await loadStatus();
        } catch (e) { toast.error(e?.response?.data?.detail || t("integr.save_failed")); }
        finally { setBusy(false); }
    };
    const runTest = async () => {
        setTestBusy(true); setTestResult(null);
        try {
            const r = await api.post("/integrations/apollo-mcp/test");
            setTestResult(r.data);
            toast.success(t("integr.mcp_test_ok", { n: r.data.count }));
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("integr.mcp_test_failed"));
        } finally { setTestBusy(false); }
    };

    const connected = !!status?.connected;
    const reauth = status?.status === "reauth_required";

    return (
        <SubSection title={`3. ${t("integr.mcp_title")}`} testid="apollo-mcp-section">
            <p className="text-[11px] text-[var(--jr-text-3)] mb-3">{t("integr.mcp_desc")}</p>
            {status === null ? (
                <p className="text-xs text-[var(--jr-text-3)] italic">{t("integr.fetching")}</p>
            ) : connected ? (
                <div className="space-y-3">
                    <div className="flex flex-wrap items-center gap-2" data-testid="apollo-mcp-status">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[var(--jr-success)]/15 border border-[var(--jr-success)]/30 text-[11px] text-[var(--jr-success)]">
                            <CheckCircle2 size={12} /> {t("integr.mcp_connected")}
                        </span>
                        {status.tools_count > 0 && (
                            <span className="text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)]">
                                {status.tools_count} tools · {status.scope_count} scopes
                            </span>
                        )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <button onClick={runTest} disabled={testBusy} data-testid="apollo-mcp-test"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-mono uppercase tracking-[0.16em] border border-white/10 text-[var(--jr-text-2)] hover:text-white hover:bg-white/5 disabled:opacity-50">
                            {testBusy ? <Loader2 size={11} className="animate-spin" /> : <FlaskConical size={11} />} {t("integr.mcp_test_btn")}
                        </button>
                        <button onClick={disconnect} disabled={busy} data-testid="apollo-mcp-disconnect"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-mono uppercase tracking-[0.16em] border border-white/10 text-[var(--jr-text-3)] hover:text-[#ef4444] disabled:opacity-50">
                            <Unplug size={11} /> {t("integr.mcp_disconnect")}
                        </button>
                    </div>
                    {testResult?.people?.length > 0 && (
                        <div className="rounded-lg border border-[var(--jr-success)]/20 bg-[var(--jr-success)]/[0.06] p-3 space-y-1"
                            data-testid="apollo-mcp-test-result">
                            <p className="text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-success)]">{t("integr.mcp_test_proof")}</p>
                            {testResult.people.map((p, i) => (
                                <p key={i} className="text-xs text-[var(--jr-text-2)]">
                                    <span className="text-white">{p.name}</span>{p.title ? ` — ${p.title}` : ""}{p.company ? ` @ ${p.company}` : ""}
                                </p>
                            ))}
                        </div>
                    )}
                </div>
            ) : (
                <div className="space-y-2">
                    {reauth && (
                        <div className="inline-flex items-start gap-2 rounded-lg border border-[#f59e0b]/30 bg-[#f59e0b]/[0.08] px-3 py-2 text-xs text-[var(--jr-text-2)]">
                            <AlertTriangle size={14} className="text-[#f59e0b] mt-0.5 shrink-0" />
                            <span>{t("integr.mcp_reauth")}</span>
                        </div>
                    )}
                    <button onClick={connect} disabled={busy} data-testid="apollo-mcp-connect"
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[var(--jr-brand)] text-black text-sm font-medium hover:bg-[var(--jr-brand-2)] disabled:opacity-50">
                        {busy ? <Loader2 size={14} className="animate-spin" /> : <Plug size={14} />} {t("integr.mcp_connect")}
                    </button>
                    <p className="text-[10px] text-[var(--jr-text-3)]">{t("integr.mcp_connect_hint")}</p>
                </div>
            )}
        </SubSection>
    );
}

/* ----------------------------------------------------------------- */
/*  Apollo vault — api_key + live credit/quota meter                 */
/* ----------------------------------------------------------------- */
function ApolloVault({ cfg, onSaved }) {
    const { t, i18n } = useTranslation();
    const [apiKey, setApiKey] = useState("");
    const [reveal, setReveal] = useState(false);
    const [busy, setBusy] = useState(false);
    const [usage, setUsage] = useState(null);
    const [usageBusy, setUsageBusy] = useState(false);

    const loadUsage = async () => {
        if (!cfg.configured) return;
        setUsageBusy(true);
        try {
            const r = await api.get("/integrations/apollo/usage");
            setUsage(r.data);
        } catch (e) { /* silent — quota probe may simply timeout */ }
        finally { setUsageBusy(false); }
    };
    useEffect(() => { loadUsage(); }, [cfg.configured]);

    const save = async () => {
        if (!apiKey.trim() && !cfg.configured) { toast.error(t("integr.enter_key")); return; }
        setBusy(true);
        try {
            await api.patch("/integrations/apollo", { api_key: apiKey });
            toast.success(t("integr.key_saved", { name: "Apollo" }));
            setApiKey(""); setReveal(false); onSaved();
            setTimeout(loadUsage, 1500);
        } catch (e) { toast.error(e?.response?.data?.detail || t("integr.save_failed")); }
        finally { setBusy(false); }
    };
    const remove = async () => {
        if (!window.confirm(t("integr.delete_key_confirm", { name: "Apollo" }))) return;
        setBusy(true);
        try { await api.delete("/integrations/apollo"); setUsage(null); toast.success(t("integr.deleted")); onSaved(); }
        catch (e) { toast.error(e?.response?.data?.detail || t("integr.delete_failed")); }
        finally { setBusy(false); }
    };

    return (
        <div className="space-y-4">
            <SubSection title={`1. ${t("integr.sec_api_key")}`} testid="apollo-credentials">
                <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">Apollo API Key</label>
                <div className="mt-1.5 flex gap-2">
                    <div className="flex-1 relative">
                        <KeyRound size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--jr-text-3)]" />
                        <input value={apiKey} onChange={(e) => setApiKey(e.target.value)}
                            placeholder={cfg.api_key_masked || t("integr.paste_key")}
                            type={reveal ? "text" : "password"}
                            data-testid="apollo-apikey-input"
                            className="w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg pl-9 pr-10 py-2.5 text-sm text-white outline-none focus:border-[var(--jr-brand)]" />
                        <button type="button" onClick={() => setReveal((r) => !r)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-[var(--jr-text-3)] hover:text-white">
                            {reveal ? <EyeOff size={14} /> : <Eye size={14} />}
                        </button>
                    </div>
                    <button onClick={save} disabled={busy} data-testid="apollo-save"
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[var(--jr-brand)] text-black text-sm font-medium hover:bg-[var(--jr-brand-2)] disabled:opacity-50">
                        {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} {t("common.save")}
                    </button>
                </div>
                <p className="mt-2 text-[10px] text-[var(--jr-text-3)]">
                    {t("integr.apollo_where")} <span className="text-[var(--jr-text-2)]">app.apollo.io → Settings → Integrations → API Keys</span>.
                    {" "}{t("integr.apollo_scopes")}
                </p>
                {cfg.configured && (
                    <button onClick={remove} className="mt-2 text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)] hover:text-[#ef4444]">
                        {t("integr.delete_current_key")}
                    </button>
                )}
            </SubSection>

            <SubSection title={`2. ${t("integr.apollo_credit_meter")}`}
                action={
                    <button onClick={loadUsage} disabled={usageBusy || !cfg.configured} data-testid="apollo-usage-refresh"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-mono uppercase tracking-[0.16em] border border-white/10 text-[var(--jr-text-2)] hover:text-white hover:bg-white/5 disabled:opacity-50">
                        {usageBusy ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />} {t("common.refresh")}
                    </button>
                }
                testid="apollo-usage-section">
                {!cfg.configured ? (
                    <p className="text-xs text-[var(--jr-text-3)] italic">{t("integr.apollo_add_key_hint")}</p>
                ) : !usage ? (
                    <p className="text-xs text-[var(--jr-text-3)] italic">{usageBusy ? t("integr.fetching") : t("integr.no_quota_info")}</p>
                ) : (
                    <div className="grid sm:grid-cols-3 gap-3" data-testid="apollo-usage-result">
                        <CreditMeter label={t("integr.per_minute")} w={usage.minute} />
                        <CreditMeter label={t("integr.per_hour")} w={usage.hour} />
                        <CreditMeter label={t("integr.per_day")} w={usage.day} highlight />
                    </div>
                )}
                {usage?.day?.pct >= 80 && (
                    <div className="mt-3 inline-flex items-start gap-2 rounded-lg border border-[var(--jr-error)]/30 bg-[var(--jr-error)]/[0.08] px-3 py-2 text-xs text-[var(--jr-text-2)]"
                        data-testid="apollo-usage-warning">
                        <AlertTriangle size={14} className="text-[var(--jr-error)] mt-0.5 shrink-0" />
                        <div>
                            <strong className="text-white">{t("integr.quota_low")}</strong> {t("integr.quota_low_body", { pct: usage.day.pct, used: usage.day.used, limit: usage.day.limit })}
                        </div>
                    </div>
                )}
                {usage?.fetched_at && (
                    <p className="mt-2 text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">
                        {t("integr.updated")}: {new Date(usage.fetched_at).toLocaleTimeString(i18n.language === "en" ? "en-GB" : i18n.language === "fr" ? "fr-BE" : "nl-BE")}
                    </p>
                )}
            </SubSection>

            <ApolloMcpSection />
        </div>
    );
}

export default ApolloVault;
